import React, { useMemo, useState } from 'react';
import { uploadBase64Selfie } from '@xoxokiosk/shared';
import SelfieCapture from '../SelfieCapture';
import LogoHeader from './LogoHeader';
import MBTIBigLetter from './MBTIBigLetter';
import NameInput from './NameInput';
import StartButton from './StartButton';

type IntroStepProps = {
  instanceId: string;
  defaultName?: string;
  defaultSelfieUrl?: string;
  onComplete: (data: { name: string; selfieUrl: string }) => void;
  language: 'en' | 'zh-HK';
  uiText: {
    title: string;
    subtitle: string;
    nameLabel: string;
    namePlaceholder: string;
    startButton: string;
  };
};

const IntroStep: React.FC<IntroStepProps> = ({ instanceId, defaultName = '', defaultSelfieUrl = '', onComplete, uiText }) => {
  const [name, setName] = useState(defaultName);
  const [selfieUrl, setSelfieUrl] = useState(defaultSelfieUrl);
  const [previewUrl, setPreviewUrl] = useState(defaultSelfieUrl);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingStart, setPendingStart] = useState(false);

  const hasName = useMemo(() => name.trim().length > 0, [name]);
  const canStart = useMemo(() => hasName && !!selfieUrl && !uploading, [hasName, selfieUrl, uploading]);

  const handleCapture = async (imageData: string) => {
    try {
      setUploading(true);
      setError(null);
      const result = await uploadBase64Selfie(imageData, instanceId);
      setSelfieUrl(result.url);
      setPreviewUrl(result.url);
      setCaptureOpen(false);

      if (pendingStart) {
        const nextName = name.trim() || 'Guest';
        onComplete({ name: nextName, selfieUrl: result.url });
        setPendingStart(false);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save selfie');
    } finally {
      setUploading(false);
    }
  };

  const handleStart = () => {
    if (!hasName || uploading) return;

    if (!selfieUrl) {
      setPendingStart(true);
      setCaptureOpen(true);
      return;
    }

    if (!canStart) return;
    const nextName = name.trim() || 'Guest';
    onComplete({ name: nextName, selfieUrl });
  };

  const handleRetake = () => {
    setPendingStart(false);
    setCaptureOpen(true);
  };

  const mbtiLetters = [
    { letter: 'M', glowColor: '#95627b' },
    { letter: 'B', glowColor: '#738d56' },
    { letter: 'T', glowColor: '#51a9aa' },
    { letter: 'I', glowColor: '#e4ae3a' },
  ];

  return (
    <div className="intro-hero">
      <LogoHeader />

      <div className="intro-hero-content">
        <div className="intro-hero-backdrop" />

        <div className="intro-hero-mbti">
          {mbtiLetters.map((item) => (
            <MBTIBigLetter
              key={item.letter}
              letter={item.letter}
              glowColor={item.glowColor}
            />
          ))}
        </div>

        <div className="intro-hero-title">Mixologist</div>

        <p className="intro-hero-tagline">{uiText.subtitle}</p>

        <NameInput
          id="guest-name"
          value={name}
          placeholder={uiText.namePlaceholder}
          onChange={setName}
        />

        <StartButton
          label={uploading ? 'Uploading selfie...' : uiText.startButton}
          disabled={!hasName || uploading}
          onClick={handleStart}
        />

        {previewUrl && (
          <button
            type="button"
            className="intro-hero-retake"
            onClick={handleRetake}
            disabled={uploading}
          >
            Retake selfie
          </button>
        )}

        {error && <div className="intro-error intro-hero-error">{error}</div>}
      </div>

      {captureOpen && (
        <div className="intro-capture-modal">
          <div className="intro-capture-card">
            <SelfieCapture
              onCapture={handleCapture}
              onCancel={() => {
                setCaptureOpen(false);
                setPendingStart(false);
              }}
              countdownSeconds={3}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroStep;
